import React from 'react';
import { Target, Eye, Users, Award, BookOpen, Globe } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: BookOpen, value: '500+', label: 'Publications' },
    { icon: Users, value: '1000+', label: 'Authors' },
    { icon: Globe, value: '50+', label: 'Countries' },
    { icon: Award, value: '25+', label: 'Awards' }
  ];

  const team = [
    {
      name: "Dr. John Smith",
      title: "Founder & CEO",
      description: "Leading academic publisher with 20+ years of experience in scholarly communication.",
      image: "https://via.placeholder.com/150x150/3b82f6/ffffff?text=JS"
    },
    {
      name: "Prof. Sarah Johnson",
      title: "Editorial Director",
      description: "Renowned researcher and editor overseeing our publication quality and standards.",
      image: "https://via.placeholder.com/150x150/10b981/ffffff?text=SJ"
    },
    {
      name: "Dr. Michael Chen",
      title: "Conference Director",
      description: "Expert in organizing international conferences and academic events worldwide.",
      image: "https://via.placeholder.com/150x150/f59e0b/ffffff?text=MC"
    }
  ];

  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh', padding: '80px 0' }}>
      <div className="container-custom">
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h1 className="heading-1" style={{ marginBottom: '24px' }}>About ESN Publications</h1>
          <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto' }}>
            We are a leading academic publishing house dedicated to advancing knowledge and fostering 
            scholarly communication across diverse fields of research. Our mission is to provide a 
            platform for researchers to share their groundbreaking work with the global academic community.
          </p>
        </div>

        {/* Mission & Vision */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '40px',
          marginBottom: '80px'
        }}>
          <div style={{ 
            backgroundColor: 'var(--bg-card)', 
            borderRadius: '16px', 
            padding: '40px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              backgroundColor: 'var(--brand-primary)', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Target size={40} color="white" />
            </div>
            <h2 className="heading-2" style={{ marginBottom: '20px' }}>Our Mission</h2>
            <p className="body-medium" style={{ lineHeight: 1.6 }}>
              To provide a premier platform for researchers and academics to publish high-quality work, 
              share knowledge, and advance their fields through collaborative research and innovation. 
              We strive to maintain the highest standards of academic integrity and excellence.
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'var(--bg-card)', 
            borderRadius: '16px', 
            padding: '40px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              backgroundColor: 'var(--brand-secondary)', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Eye size={40} color="white" />
            </div>
            <h2 className="heading-2" style={{ marginBottom: '20px' }}>Our Vision</h2>
            <p className="body-medium" style={{ lineHeight: 1.6 }}>
              To be the leading global platform for academic publishing and research collaboration, 
              fostering innovation and excellence in scholarly communication across all disciplines. 
              We envision a world where knowledge flows freely and research drives positive change.
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div style={{ 
          backgroundColor: 'var(--bg-card)', 
          borderRadius: '16px', 
          padding: '60px 40px',
          border: '1px solid var(--border-light)',
          marginBottom: '80px'
        }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '48px' }}>
            Our Impact in Numbers
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '40px',
            textAlign: 'center'
          }}>
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <IconComponent size={40} color="white" />
                  </div>
                  <div style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--brand-primary)', marginBottom: '8px' }}>
                    {stat.value}
                  </div>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Our Story */}
        <div style={{ marginBottom: '80px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '48px' }}>Our Story</h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h3 className="heading-3" style={{ marginBottom: '20px' }}>Founded on Excellence</h3>
              <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.6 }}>
                ESN Publications was founded in 2010 with a simple yet ambitious goal: to create a 
                platform where researchers could share their work with the world without barriers. 
                What started as a small academic initiative has grown into a globally recognized 
                publishing house.
              </p>
              <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.6 }}>
                Over the years, we have published thousands of research papers, organized numerous 
                international conferences, and built a community of scholars spanning across continents. 
                Our commitment to quality and innovation has earned us recognition from academic 
                institutions worldwide.
              </p>
              <p className="body-medium" style={{ lineHeight: 1.6 }}>
                Today, we continue to evolve, embracing new technologies and methodologies to better 
                serve the academic community while maintaining our core values of integrity, excellence, 
                and accessibility.
              </p>
            </div>
            
            <div style={{ 
              backgroundColor: 'var(--bg-card)', 
              borderRadius: '12px', 
              padding: '40px',
              border: '1px solid var(--border-light)'
            }}>
              <h4 className="heading-3" style={{ marginBottom: '24px', textAlign: 'center' }}>
                Key Milestones
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%'
                  }} />
                  <div>
                    <strong>2010:</strong> ESN Publications founded
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%'
                  }} />
                  <div>
                    <strong>2015:</strong> First international conference organized
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%'
                  }} />
                  <div>
                    <strong>2018:</strong> 1000+ authors milestone reached
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%'
                  }} />
                  <div>
                    <strong>2021:</strong> Digital transformation completed
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: 'var(--brand-primary)', 
                    borderRadius: '50%'
                  }} />
                  <div>
                    <strong>2024:</strong> Global expansion to 50+ countries
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Leadership Team */}
        <div>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '48px' }}>
            Leadership Team
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '40px'
          }}>
            {team.map((member, index) => (
              <div key={index} style={{ 
                backgroundColor: 'var(--bg-card)', 
                borderRadius: '12px', 
                padding: '32px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}>
                <img 
                  src={member.image} 
                  alt={member.name}
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%',
                    marginBottom: '20px',
                    objectFit: 'cover'
                  }}
                />
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>{member.name}</h3>
                <p style={{ color: 'var(--brand-primary)', fontWeight: 600, marginBottom: '16px' }}>
                  {member.title}
                </p>
                <p className="body-medium" style={{ lineHeight: 1.6 }}>
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;